import React, { useState, useEffect } from "react";
import { Typography, Button } from "@mui/material";
import { Check, Loader2 } from "lucide-react";
import { useMutation } from "@tanstack/react-query";
import { LoginParams, LoginResponse } from "@lib/types/login";
import { loginUser } from "@lib/api/mutations/auth";
import { AxiosError } from "axios";
import { Role, useAuth } from "@context/AuthContext";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import useHandleError from "@lib/utils/errorHook";
import { handleWialonLogin } from "@lib/utils/login";

const WialonLoginRedirect: React.FC = () => {
  const { login } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleError = useHandleError();

  const [error, setError] = useState<string>("");
  const [showLoginButton, setShowLoginButton] = useState<boolean>(false);

  const [countdown, setCountdown] = useState(10);

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const token = queryParams.get("access_token");

    if (token) {
      handleLogin(token);
    } else {
      setError("Invalid Wialon Token");
    }
  }, []);

  const {
    mutate: loginMutation,
    isError: isLoginError,
    isPending: isLoginLoading,
  } = useMutation({
    mutationFn: (data: LoginParams) => loginUser(data),
    onMutate: () => {},
    onSuccess: (data: LoginResponse) => {
      login(
        Role.User,
        data.token,
        `${data.info.first_name} ${data.info.last_name}`
      );

      navigate("/user/dashboard");
    },
    onError: (error: AxiosError | unknown) => {
      if (error instanceof AxiosError && error.response) {
        const errorDetails = handleError(error.response.data);

        if (error.response.data.error_code === "LOGIN_NOT_FOUND") {
          setShowLoginButton(true);
        } else {
          setShowLoginButton(false);
        }
        errorDetails && setError(errorDetails.message);
      } else {
        setError(t("general.error"));
      }
    },
  });

  const handleLogin = (token: string) => {
    loginMutation({ token });
  };

  useEffect(() => {
    if (showLoginButton) {
      setCountdown(10);

      const interval = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            handleWialonLogin();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [showLoginButton]);

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-100">
      <div className="w-full max-w-sm p-8 bg-white rounded-lg shadow-lg">
        <Typography variant="h4" className="text-center mb-6 text-gray-800">
          {isLoginLoading && t("user.login.loggingIn")}
          {!isLoginLoading && !error && (
            <div className="flex flex-col">
              <Check className="w-10 h-10 text-green-500 self-center" />
              {t("user.login.loggedIn")}
            </div>
          )}
          {!isLoginLoading && error && t("user.login.loginFailed")}
        </Typography>

        {isLoginLoading && (
          <div className="flex justify-center">
            <Loader2 className="animate-spin w-10 h-10 text-blue-500" />
          </div>
        )}

        {!isLoginLoading && error && (
          <div className="flex flex-col gap-5 justify-center">
            <Typography variant="body1" className="text-red-500 text-center">
              {error}
            </Typography>
            {showLoginButton && (
              <Button
                onClick={handleWialonLogin}
                variant="contained"
                fullWidth
                className="p-4 mb-4"
              >
                {t("user.login.redirectToLogin")} ({countdown}s)
              </Button>
            )}
          </div>
        )}

        {!isLoginLoading && !error && (
          <div className="text-center">
            <Typography variant="body1">{t("user.login.success")}</Typography>
          </div>
        )}
      </div>
    </div>
  );
};

export default WialonLoginRedirect;
